// admin-api/lib/padrekko-qc/caseStages.js
//
// THE CASE STAGE TABLES. One declaration of each, and the reason they are here
// rather than in the four files that used to carry their own.
//
// ── WHAT WAS WRONG ──────────────────────────────────────────────────────────
//
// REM-301. Three tables, nine declarations, and one pair that did not even
// share a name:
//
//   STAGE_WRITER        5 copies  requireCaseAccess.js, padrekko-qc.mjs,
//                                 padrekko-attachments.mjs,
//                                 padrekko-map-annotations.mjs, useQcStore.js
//   STAGE_NEXT          2 copies  padrekko-qc.mjs, useQcStore.js
//   STAGE_REVERSE_PREV  2 copies  padrekko-qc.mjs as STAGE_REVERSE_PREV,
//                                 useQcStore.js as STAGE_PREV
//
// All nine agreed at the time they were collapsed. They agreed by care, not by
// construction, and the failure mode of a divergence is NOT a crash:
//
//   a stale STAGE_WRITER in padrekko-attachments.mjs lets the wrong org upload
//   evidence to a case while requireCaseAccess believes it refused it;
//
//   a stale copy in the UI renders controls the server then rejects, which
//   reads to a user as a broken app rather than as a permissions boundary —
//   REM-63's exact symptom.
//
// ── WHY admin-api/lib/ AND NOT A SHARED TOP-LEVEL DIRECTORY ────────────────
//
// This follows homeRoute.js, which is the tree's existing answer to "a rule
// both sides need". That module states the shape: it lives in admin-api/lib/
// because it is server logic, it is kept dependency-free so it can be imported
// from outside, and the UI side is reconciled against it rather than given a
// second copy to maintain.
//
// THE PRECEDENT DOES NOT EXTEND TO A RUNTIME IMPORT, AND THE REASON IS THE
// BUILD, not taste. Both images are built from directory-scoped contexts —
// `context: ./ui` with `COPY . .`, and `context: ./admin-api` with explicit
// `COPY lib ./lib` — so neither image contains the other's source. A runtime
// `import "../../admin-api/lib/..."` from ui/src resolves on a developer's
// disk and fails inside `docker compose build ui`, which is the worst of both:
// green locally, broken in the artifact. Measured, not assumed — see the
// commit that added this file.
//
// So the server imports this module directly, and the UI's copy is reconciled
// against it by a check that runs on every commit rather than by a human
// remembering. That is exactly what homeRoute.js does: its only ui/src
// importer is a TEST (router.authMeContract.test.jsx), never runtime code.
//
// ── ORDER IS A PROPERTY OF THIS FILE ───────────────────────────────────────
//
// STAGE_NEXT and STAGE_REVERSE_PREV are inverses and are written out rather
// than derived from one another, because a derived inverse hides the terminal
// cases: `complete` has no next and `dh_review` has no previous, and both are
// meaningful nulls rather than gaps. Stated, they can be read; computed, they
// would have to be trusted.

/**
 * Case stage → the participant role that may write at that stage.
 *
 * `complete` maps to null: no role writes, which is what makes a closed case
 * read-only for everyone including its owner. Read access is unaffected —
 * requireCaseAccess allows GET for every participant at every stage.
 */
export const STAGE_WRITER = Object.freeze({
  dh_review:       "dh_analyst",
  customer_review: "customer_analyst",
  operator_review: "operator_analyst",
  complete:        null,
});

/** Case stage → the next stage forward. `complete` is the end of the line. */
export const STAGE_NEXT = Object.freeze({
  dh_review:       "customer_review",
  customer_review: "operator_review",
  operator_review: "complete",
  complete:        null,
});

/**
 * Case stage → the stage /reverse-stage may return to.
 *
 * `dh_review` has no previous — the first stage cannot be reversed out of, and
 * a caller gets a 422. `complete` is null here too, deliberately: reversing out
 * of a closed case is /reopen's job, not this table's, because reopening is a
 * different authorization question.
 */
export const STAGE_REVERSE_PREV = Object.freeze({
  dh_review:       null,
  customer_review: "dh_review",
  operator_review: "customer_review",
  complete:        null,
});

/** The role that may write at `stage`, or null when nobody may. */
export function writerRoleForStage(stage) {
  return STAGE_WRITER[stage] ?? null;
}

/* ═══════════════════════════════════════════════════════════════════════════
   THE WRITER PREDICATE — STAGE_WRITER PLUS THE ONE CASE IT CANNOT EXPRESS.
   ═══════════════════════════════════════════════════════════════════════════

   STAGE_WRITER maps a stage to A ROLE. A same-tenant case (migration 111,
   REM-317) has one organisation as both owner and respondent, holding ONE
   participant row — `customer_analyst`. At `operator_review` the table says
   `operator_analyst` and there is no such org, so the table alone refuses the
   only party who could act.

   ── WHY A FUNCTION AND NOT A SECOND TABLE ─────────────────────────────────

   A `STAGE_WRITER_SAME_TENANT` beside the first would be two tables that must
   agree about three of their four rows, which is REM-301's shape reintroduced
   one file over. The variation is a SINGLE CELL — operator_review — and a
   function states that rather than duplicating the other three.

   ── EVERY WRITEABILITY DECISION MUST COME THROUGH HERE ────────────────────

   Seven sites read STAGE_WRITER[stage] === role inline before this existed:

     middleware/requireCaseAccess.js        the central gate
     routes/padrekko-attachments.mjs        uploads
     routes/padrekko-map-annotations.mjs    annotations
     routes/padrekko-qc.mjs  (x3)           advance, override, dispute ruling
     ui/src/stores/useQcStore.js            the control the user sees

   AN INCOMPLETE ADOPTION IS INVISIBLE AND IS THE WHOLE RISK: the gate allows,
   attachments refuse, and the user is shown a control that fails. That is
   REM-63's symptom arriving from a new direction, and it is why
   `mayWriteAtStage` is in checkStageTablesSingleSource's NAMES — a
   re-declaration anywhere under admin-api/ or ui/src now fails the commit.

   ── DH GAINS NOTHING, AND `complete` STAYS CLOSED ─────────────────────────

   The same-tenant branch admits exactly one (stage, role) pair that the table
   refuses. dh_analyst's stage is unchanged, and `complete` is terminal for
   everyone including a same-tenant owner — a closed case is read-only, which
   is what makes the post-close steward rule meaningful.
   ═══════════════════════════════════════════════════════════════════════════ */

/**
 * May `role` write at `stage`?
 *
 * @param {object} p
 * @param {string} p.stage            padrekko_qc_cases.current_stage
 * @param {string|null} p.role        the caller's participant role
 * @param {boolean} [p.sameTenant]    padrekko_qc_cases.same_tenant
 * @returns {boolean}
 */
export function mayWriteAtStage({ stage, role, sameTenant = false }) {
  if (!role) return false;
  const writer = STAGE_WRITER[stage] ?? null;
  if (writer === null) return false;          // `complete` — nobody, ever.
  if (writer === role) return true;
  // THE ONE ADDED TRUTH. The owner answers at operator_review because there is
  // no respondent org to answer instead.
  return sameTenant === true
      && stage === "operator_review"
      && role === "customer_analyst";
}

/**
 * The role that may write at `stage` FOR THIS CASE — the same rule as
 * mayWriteAtStage, expressed as the answer to "who?" rather than "may I?".
 *
 * Kept beside it rather than derived by the caller, because a caller that
 * computed it from STAGE_WRITER would be the eighth inline copy.
 */
export function writerRoleForCase(stage, sameTenant = false) {
  if (sameTenant === true && stage === "operator_review") return "customer_analyst";
  return STAGE_WRITER[stage] ?? null;
}

/** The next stage forward from `stage`, or null at the end. */
export function nextStageFor(stage) {
  return STAGE_NEXT[stage] ?? null;
}

/** The stage /reverse-stage may return to from `stage`, or null. */
export function previousStageFor(stage) {
  return STAGE_REVERSE_PREV[stage] ?? null;
}

/* ═══════════════════════════════════════════════════════════════════════════
   RELEASE, SHARE, DEADLINE — 099's facts, and the one derivation over them.
   ═══════════════════════════════════════════════════════════════════════════

   These sit beside the stage tables rather than in a second module because
   they answer the same kind of question — where is this case, who acted, what
   is expected next — and REM-301 is a standing argument against a second file
   that a reader has to know exists.

   THE DEADLINE OPTIONS ARE BOUNDED AND SHARE NO TOKEN WITH next_observation_basis
   (deadline | routine), so a value names its own column. That is 098's rule,
   applied to the second pair of columns that could have been confused.
*/

/** The deadline options an owner may choose, and the days each resolves to. */
export const RESPONSE_DEADLINE_DAYS = Object.freeze({
  days_14: 14,
  days_30: 30,
  days_60: 60,
  days_90: 90,
});

/** True when `basis` is one of the bounded options. */
export function isResponseDeadlineBasis(basis) {
  return Object.prototype.hasOwnProperty.call(RESPONSE_DEADLINE_DAYS, basis);
}

/**
 * Resolve a deadline date from a basis and the date the case was shared.
 *
 * ANCHORED ON SHARE, not on when the owner picked the basis. The respondent's
 * clock starts when they receive the case; a deadline measured from a decision
 * they were not party to would be a date nobody told them about.
 *
 * @param {string} basis      one of RESPONSE_DEADLINE_DAYS
 * @param {Date}   sharedAt   when the case reached the respondent
 * @returns {string|null}     YYYY-MM-DD, or null for an unknown basis
 */
export function resolveResponseDeadline(basis, sharedAt) {
  const days = RESPONSE_DEADLINE_DAYS[basis];
  if (days === undefined || !(sharedAt instanceof Date) || Number.isNaN(+sharedAt)) return null;
  const d = new Date(sharedAt.getTime());
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/* ── NON-RESPONSE IS DERIVED. THERE IS NO STORED FLAG, DELIBERATELY. ───────
 *
 * A case is in non-response when all three hold:
 *
 *   1. it was SHARED               — the respondent actually received it
 *   2. the DEADLINE has PASSED     — there was a date and it is behind us
 *   3. nobody has RESPONDED        — no finding carries a completion decision
 *
 * A stored flag would need a maintainer, and the only honest maintainer is a
 * clock: between the deadline passing and whatever job noticed, the column
 * would be wrong, and wrong in the direction that under-reports. Derived, it
 * is correct at read time by construction and there is nothing to drift.
 *
 * "RESPONDED" IS A COMPLETION DECISION, NOT A PASS COUNT. `operator_pass_count`
 * increments only on the operator_review -> customer_review handback, so it
 * answers "did they hand the case back", not "did they say anything about the
 * work". A respondent who marks half the findings and never hands back has
 * responded; that counter would call them silent.
 *
 * NOT COUNTED AS A RESPONSE: a verification. That is the OWNER's act, and an
 * owner cannot answer on the respondent's behalf.
 */

/** SQL twin. Requires the cases table aliased `c`. */
export const NON_RESPONSE_SQL = `(
  c.shared_at IS NOT NULL
  AND c.response_deadline_date IS NOT NULL
  AND c.response_deadline_date < current_date
  AND NOT EXISTS (
    SELECT 1
      FROM aiprime.padrekko_qc_findings nrf
      JOIN aiprime.padrekko_qc_pads nrp ON nrp.id = nrf.pad_id
     WHERE nrp.session_id = c.session_id
       AND NOT nrf.is_removed
       AND nrf.completion_status <> 'pending'
  )
)`;

/**
 * A calendar day as YYYY-MM-DD, whatever shape the caller got it in.
 *
 * ── WHY THIS EXISTS: THE JS TWIN NEVER WORKED AGAINST THE DATABASE ────────
 *
 * node-postgres parses a `date` column (OID 1082) into a JS **Date**, not a
 * string. The original twin did `String(value).slice(0, 10)`, which on a Date
 * yields "Mon Sep 13" — an invalid date, so `due` was NaN, `NaN < today` was
 * false, and isNonResponse returned false for EVERY overdue case.
 *
 * It shipped that way in 099 and nothing caught it, because the function had
 * no callers: 15f was additive and left the derivation unwired. It was found
 * the first time a fixture drove a real row through it (15g9) — REM-307.
 *
 * Reads the LOCAL components of a Date rather than toISOString(): pg builds a
 * date at local midnight, so in any timezone behind UTC toISOString() would
 * report the previous day and move a deadline by one.
 */
function isoDay(value) {
  if (value instanceof Date) {
    if (Number.isNaN(+value)) return null;
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, "0");
    const d = String(value.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  const s = String(value).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;
}

/**
 * JS twin of NON_RESPONSE_SQL. `responded` is supplied by the caller because
 * it is a fact about findings, not about the case row.
 *
 * @param {{shared_at: *, response_deadline_date: *}} caseRow
 * @param {boolean} responded  any active finding carries a completion decision
 * @param {Date}    [now]
 */
export function isNonResponse(caseRow, responded, now = new Date()) {
  if (!caseRow?.shared_at) return false;
  if (!caseRow?.response_deadline_date) return false;
  if (responded) return false;
  const due = isoDay(caseRow.response_deadline_date);
  const today = isoDay(now);
  if (!due || !today) return false;
  // String comparison is exact for ISO days and needs no Date arithmetic.
  return due < today;
}
